import React, { useState } from 'react';
import styled from 'styled-components';
import { INGEST_ENDPOINT, buildRobloxSnippet } from '../analyticsUtils';
import {
  Btn,
  Card,
  CardHead,
  CardTitle,
  Check,
  Code,
  DangerBtn,
  Field,
  GhostBtn,
  Hint,
  Input,
  Mono,
  Notice,
  Row,
  Select,
  Stack,
  Tab,
  Tabs,
} from '../AnalyticsAdmin.styles';
import { SessionMap } from './SessionsView';

export const SETTINGS_TABS = [
  { id: 'general', label: 'General' },
  { id: 'map', label: 'Map' },
  { id: 'connect', label: 'Connect game' },
  { id: 'data', label: 'Data & danger zone' },
];

const Narrow = styled.div`
  max-width: 47.5rem;
`;

const Big = styled.span`
  font-family: var(--font-display);
  font-size: 1.375rem;
  font-weight: 600;
`;

// Custom-drawn slider: native range tracks render as a white bar in dark Chrome.
const thumb = `
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--brand);
  border: 3px solid var(--card);
  box-shadow: 0 0 0 1px var(--brand);
  cursor: pointer;
`;

const Range = styled.input.attrs({ type: 'range' })`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 6px;
  margin: 0.5rem 0;
  border-radius: 999px;
  background: linear-gradient(to right, var(--brand) ${(p) => p.$pct}%, var(--border-accent) ${(p) => p.$pct}%);
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    ${thumb}
  }

  &::-moz-range-thumb {
    ${thumb}
  }

  &::-moz-range-track {
    background: transparent;
  }

  &:focus-visible {
    outline: 2px solid var(--brand);
    outline-offset: 6px;
  }
`;

const RangeScale = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: var(--font-mono);
  font-size: 0.6875rem;
  color: var(--text-tertiary);
`;

const DropZone = styled.label`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.875rem 1rem;
  padding: 1rem;
  border: 1px dashed ${(p) => (p.$over ? 'var(--brand)' : 'var(--border-accent)')};
  border-radius: 0.5rem;
  background: ${(p) => (p.$over ? 'rgba(255, 122, 46, 0.06)' : 'var(--background)')};
  cursor: pointer;
  transition: border-color var(--transition-fast), background var(--transition-fast);

  &:hover {
    border-color: var(--text-tertiary);
  }

  &:focus-within {
    border-color: var(--brand);
  }

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
  }
`;

const FakeButton = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 2.25rem;
  padding: 0 0.875rem;
  border: 1px solid var(--border-accent);
  border-radius: 0.375rem;
  background: var(--card);
  color: var(--text-primary);
  font-size: 0.8125rem;
  font-weight: 500;
  white-space: nowrap;
`;

const MapImagePicker = ({ file, onChange, hasImage }) => {
  const [over, setOver] = React.useState(false);
  return (
    <DropZone
      $over={over}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const dropped = e.dataTransfer.files?.[0];
        if (dropped && /^image\/(png|jpeg|webp)$/.test(dropped.type)) onChange(dropped);
      }}
    >
      <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => onChange(e.target.files?.[0] || null)} />
      <FakeButton aria-hidden="true">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 16V4M7 9l5-5 5 5M5 20h14" />
        </svg>
        {hasImage || file ? 'Replace image' : 'Choose image'}
      </FakeButton>
      <span style={{ minWidth: 0, flex: '1 1 12rem' }}>
        <span style={{ display: 'block', fontSize: '0.875rem', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {file ? file.name : hasImage ? 'A map image is uploaded' : 'No image yet'}
        </span>
        <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
          {file ? 'Will be uploaded when you save the map' : 'PNG, JPG or WebP · click or drop a file here'}
        </span>
      </span>
    </DropZone>
  );
};

const CredRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0;
  border-top: 1px solid var(--border-subtle);

  > span:first-child {
    width: 5.75rem;
    flex-shrink: 0;
    font-size: 0.8125rem;
    color: var(--text-tertiary);
  }

  code {
    flex-grow: 1;
    min-width: 0;
    font-family: var(--font-mono);
    font-size: 0.8125rem;
    color: var(--text-code);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const DangerZone = styled.div`
  border: 1px solid rgba(240, 82, 82, 0.35);
  border-radius: 0.75rem;
  overflow: hidden;

  > div:first-child {
    padding: 0.75rem 1.375rem;
    background: rgba(240, 82, 82, 0.06);
    border-bottom: 1px solid rgba(240, 82, 82, 0.25);
    font-family: var(--font-mono);
    font-size: 0.75rem;
    color: #f07a7a;
  }
`;

const DangerRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 1.125rem 1.375rem;

  & + & {
    border-top: 1px solid var(--border);
  }

  b {
    display: block;
    font-weight: 600;
  }

  small {
    font-size: 0.8125rem;
    color: var(--text-tertiary);
  }
`;

const ROTATIONS = [0, 90, 180, 270];

const ProjectSettingsView = ({
  tab,
  onTabChange,
  project,
  canManage,
  copyText,
  // general
  retentionInput,
  setRetentionInput,
  retentionCurrent,
  retentionMax,
  retentionSaving,
  onSaveRetention,
  sessionLimitInput,
  setSessionLimitInput,
  sessionLimitCurrent,
  sessionLimitMax,
  sessionLimitSaving,
  onSaveSessionLimit,
  onRenameProject,
  // map
  mapBounds,
  setMapBounds,
  mapFile,
  setMapFile,
  mapSaving,
  onSaveMap,
  debugPosition,
  setDebugPosition,
  mapProps,
  // connect
  revealedKey,
  onRotateKey,
  // data
  sessionsTotalCount,
  exportMode,
  setExportMode,
  exportCount,
  setExportCount,
  includeAiPrompt,
  setIncludeAiPrompt,
  includePositions,
  setIncludePositions,
  exportLoading,
  onExport,
  bulkDeleteAgeDays,
  setBulkDeleteAgeDays,
  bulkDeleteLoading,
  onBulkDelete,
  onDeleteProject,
}) => {
  const [copied, setCopied] = useState('');
  const copy = (id, text) => {
    copyText(text);
    setCopied(id);
    setTimeout(() => setCopied((c) => (c === id ? '' : c)), 1500);
  };

  const retentionValue = Number(retentionInput || retentionCurrent);
  const apiKey = revealedKey || 'YOUR_API_KEY';
  const snippet = buildRobloxSnippet(project.id, apiKey);

  return (
    <>
      <Tabs role="tablist" aria-label="Project settings">
        {SETTINGS_TABS.map((t) => (
          <Tab key={t.id} type="button" role="tab" aria-selected={tab === t.id} $active={tab === t.id} onClick={() => onTabChange(t.id)}>
            {t.label}
          </Tab>
        ))}
      </Tabs>

      <Narrow>
        {tab === 'general' && (
          <Stack>
            <Card>
              <Row $justify="space-between">
                <div>
                  <CardTitle>Project name</CardTitle>
                  <Hint $mb="0">{project.name}</Hint>
                </div>
                {canManage && (
                  <GhostBtn type="button" $small onClick={onRenameProject}>
                    Rename
                  </GhostBtn>
                )}
              </Row>
            </Card>

            <Card as="form" onSubmit={onSaveRetention}>
              <Row $justify="space-between" $align="baseline">
                <CardTitle>Keep raw session data for</CardTitle>
                <Big>
                  {retentionValue} day{retentionValue === 1 ? '' : 's'}
                </Big>
              </Row>
              <Hint>
                Older flushes are deleted automatically; funnels and heatmaps keep their totals. Your plan allows up to {retentionMax} days.
              </Hint>
              <Range
                min="1"
                max={retentionMax}
                step="1"
                value={retentionValue}
                onChange={(e) => setRetentionInput(e.target.value)}
                aria-label="Retention in days"
                $pct={retentionMax > 1 ? ((retentionValue - 1) / (retentionMax - 1)) * 100 : 100}
              />
              <RangeScale aria-hidden="true">
                <span>1 day</span>
                <span>{retentionMax} days</span>
              </RangeScale>
              <Row $justify="flex-end" style={{ marginTop: '1rem' }}>
                <Btn
                  type="submit"
                  disabled={retentionSaving || !retentionInput || Number(retentionInput) < 1 || Number(retentionInput) > retentionMax}
                >
                  {retentionSaving ? 'Saving…' : 'Save'}
                </Btn>
              </Row>
            </Card>

            <Card as="form" onSubmit={onSaveSessionLimit}>
              <CardTitle>Stored sessions limit</CardTitle>
              <Hint>
                Keeps only the most recent sessions. New sessions are rejected once the limit is reached. Plan max {sessionLimitMax.toLocaleString()}.
              </Hint>
              <Row>
                <Input
                  type="number"
                  min="1"
                  max={sessionLimitMax}
                  step="1"
                  aria-label="Maximum stored sessions"
                  value={sessionLimitInput || sessionLimitCurrent}
                  onChange={(e) => setSessionLimitInput(e.target.value)}
                  $grow
                />
                <Btn
                  type="submit"
                  disabled={sessionLimitSaving || !sessionLimitInput || Number(sessionLimitInput) < 1 || Number(sessionLimitInput) > sessionLimitMax}
                >
                  {sessionLimitSaving ? 'Saving…' : 'Save'}
                </Btn>
              </Row>
            </Card>
          </Stack>
        )}

        {tab === 'map' && (
          <Stack as="form" onSubmit={onSaveMap}>
            <Card>
              <CardTitle>Map image</CardTitle>
              <Hint>A top-down screenshot of your place. Used by session replay and heatmaps.</Hint>
              <MapImagePicker file={mapFile} onChange={setMapFile} hasImage={Boolean(mapProps?.projectMap?.hasImage)} />
            </Card>

            <Card>
              <CardTitle>World bounds</CardTitle>
              <Hint>The world X/Z coordinates at the edges of the image. They are used to place each position correctly on the map.</Hint>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(10rem, 1fr))', gap: '0.75rem' }}>
                {[
                  ['worldMinX', 'World min X'],
                  ['worldMaxX', 'World max X'],
                  ['worldMinZ', 'World min Z'],
                  ['worldMaxZ', 'World max Z'],
                ].map(([key, label]) => (
                  <Field key={key}>
                    {label}
                    <Input
                      type="number"
                      step="any"
                      $mono
                      value={mapBounds[key]}
                      onChange={(e) => setMapBounds((prev) => ({ ...prev, [key]: e.target.value }))}
                    />
                  </Field>
                ))}
              </div>
              <Row $gap="0.75rem 1.5rem" $align="flex-end" style={{ marginTop: '1rem' }}>
                <Field>
                  Rotation
                  <Select
                    value={ROTATIONS.includes(Number(mapBounds.rotationDegrees)) ? String(Number(mapBounds.rotationDegrees)) : 'custom'}
                    onChange={(e) => e.target.value !== 'custom' && setMapBounds((prev) => ({ ...prev, rotationDegrees: Number(e.target.value) }))}
                  >
                    {ROTATIONS.map((r) => (
                      <option key={r} value={r}>{r}°</option>
                    ))}
                    {!ROTATIONS.includes(Number(mapBounds.rotationDegrees)) && <option value="custom">{mapBounds.rotationDegrees}°</option>}
                  </Select>
                </Field>
                <Check>
                  <input type="checkbox" checked={Boolean(mapBounds.flipX)} onChange={(e) => setMapBounds((prev) => ({ ...prev, flipX: e.target.checked }))} />
                  Flip X
                </Check>
                <Check>
                  <input type="checkbox" checked={Boolean(mapBounds.flipZ)} onChange={(e) => setMapBounds((prev) => ({ ...prev, flipZ: e.target.checked }))} />
                  Flip Z
                </Check>
              </Row>
            </Card>

            <Card>
              <CardTitle>Check the alignment</CardTitle>
              <Hint>Enter a world position you know; a red marker shows where it lands on the map below.</Hint>
              <Row style={{ marginBottom: '1rem' }}>
                <Input
                  type="number"
                  step="any"
                  $mono
                  placeholder="X"
                  aria-label="World X"
                  value={debugPosition.x}
                  onChange={(e) => setDebugPosition((prev) => ({ ...prev, x: e.target.value }))}
                  style={{ width: '8rem' }}
                />
                <Input
                  type="number"
                  step="any"
                  $mono
                  placeholder="Z"
                  aria-label="World Z"
                  value={debugPosition.z}
                  onChange={(e) => setDebugPosition((prev) => ({ ...prev, z: e.target.value }))}
                  style={{ width: '8rem' }}
                />
              </Row>
              <SessionMap {...mapProps} projectedMapPoints={[]} showPlayer={false} />
            </Card>

            <Row $justify="flex-end">
              <Btn type="submit" disabled={mapSaving}>
                {mapSaving ? 'Saving…' : 'Save map'}
              </Btn>
            </Row>
          </Stack>
        )}

        {tab === 'connect' && (
          <Stack>
            <Card>
              <CardTitle>Connect your game</CardTitle>
              <Hint $mb="1.125rem">Paste the snippet into a server Script. New sessions appear here within a minute.</Hint>
              {[
                ['endpoint', 'Endpoint', INGEST_ENDPOINT],
                ['pid', 'Project ID', project.id],
                ['key', 'API key', revealedKey || 'Hidden. Rotate the key to get a new one'],
              ].map(([id, label, value]) => (
                <CredRow key={id}>
                  <span>{label}</span>
                  <code>{value}</code>
                  {(id !== 'key' || revealedKey) && (
                    <GhostBtn type="button" $small onClick={() => copy(id, value)}>
                      {copied === id ? 'Copied' : 'Copy'}
                    </GhostBtn>
                  )}
                </CredRow>
              ))}
              {revealedKey && (
                <Notice $tone="warning" role="status" style={{ marginTop: '0.75rem' }}>
                  Copy this key now, because it is shown only once. The previous key no longer works.
                </Notice>
              )}
            </Card>

            <Card $flush>
              <CardHead>
                <Mono>Roblox snippet</Mono>
                <GhostBtn type="button" $small onClick={() => copy('snippet', snippet)}>
                  {copied === 'snippet' ? 'Copied' : 'Copy snippet'}
                </GhostBtn>
              </CardHead>
              <Code style={{ border: 0, borderRadius: 0 }}>{snippet}</Code>
            </Card>

            <Card>
              <Row $justify="space-between">
                <div>
                  <CardTitle>Rotate API key</CardTitle>
                  <Hint $mb="0">Use this if the key leaked. The current key stops working immediately.</Hint>
                </div>
                <GhostBtn type="button" onClick={onRotateKey}>
                  Rotate key
                </GhostBtn>
              </Row>
            </Card>
          </Stack>
        )}

        {tab === 'data' && (
          <Stack>
            <Card>
              <CardTitle>Export sessions</CardTitle>
              <Hint>Download raw session data as a JSON file. The file can be analysed with your own tools or with an AI model.</Hint>
              <Row $gap="0.75rem 1.25rem" $align="flex-end">
                <Field>
                  Which sessions
                  <Select value={exportMode} onChange={(e) => setExportMode(e.target.value)}>
                    <option value="all">All {sessionsTotalCount.toLocaleString()} sessions</option>
                    <option value="count">Most recent…</option>
                  </Select>
                </Field>
                {exportMode === 'count' && (
                  <Field>
                    How many
                    <Input type="number" min="1" step="1" value={exportCount} onChange={(e) => setExportCount(e.target.value)} style={{ width: '7rem' }} />
                  </Field>
                )}
                <Check>
                  <input type="checkbox" checked={includeAiPrompt} onChange={(e) => setIncludeAiPrompt(e.target.checked)} />
                  Include AI prompt
                </Check>
                <Check>
                  <input type="checkbox" checked={includePositions} onChange={(e) => setIncludePositions(e.target.checked)} />
                  Include positions
                </Check>
                <Btn
                  type="button"
                  style={{ marginLeft: 'auto' }}
                  onClick={onExport}
                  disabled={exportLoading || (exportMode === 'count' && (!exportCount || Number(exportCount) < 1))}
                >
                  {exportLoading ? 'Preparing…' : 'Download JSON'}
                </Btn>
              </Row>
            </Card>

            <DangerZone>
              <div>DANGER ZONE</div>
              <DangerRow>
                <div>
                  <b>Delete old sessions</b>
                  <small>Removes every session (and its raw data) older than the age you pick.</small>
                </div>
                <Row>
                  <Select aria-label="Age" value={bulkDeleteAgeDays} onChange={(e) => setBulkDeleteAgeDays(e.target.value)}>
                    <option value="1">Older than 1 day</option>
                    <option value="3">Older than 3 days</option>
                    <option value="7">Older than 7 days</option>
                  </Select>
                  <DangerBtn type="button" onClick={onBulkDelete} disabled={bulkDeleteLoading}>
                    {bulkDeleteLoading ? 'Deleting…' : 'Delete…'}
                  </DangerBtn>
                </Row>
              </DangerRow>
              {canManage && (
                <DangerRow>
                  <div>
                    <b>Delete this project</b>
                    <small>All sessions, funnels, tracked stats and the map are removed. The API key stops working.</small>
                  </div>
                  <DangerBtn type="button" $solid onClick={onDeleteProject}>
                    Delete project…
                  </DangerBtn>
                </DangerRow>
              )}
            </DangerZone>
          </Stack>
        )}
      </Narrow>
    </>
  );
};

export default ProjectSettingsView;
